import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Collection } from 'firestorter';

import { Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CollectionName from '../../utils/collections';
import { replyTime } from '../../utils/threadSeen';
import { THREAD_REPLY_PAGE_SIZE } from '../../Model/ChatChannel';
import ChatMessage from '../../Model/ChatMessage';
import ChannelMessageInput from '../ChannelMessageInput';
import LoadingPage from '../LoadingPage';

import {
  Header,
  Title,
  CloseButton,
  ParentSection,
  RepliesSection,
  LoadEarlierButton,
} from './styles';
import texts from './texts.json';

const ThreadModalContent = observer(({ rootMessage, renderMessage }) => {
  const [replyLimit, setReplyLimit] = useState(THREAD_REPLY_PAGE_SIZE);

  // Newest first so the cap keeps the most recent replies; reversed for display. Growing the limit is
  // how older replies come in — a cursor taken from an earlier page would point at a document the
  // live query has since evicted, leaving it in no page at all.
  const replies = useMemo(() => new Collection(CollectionName.CHAT_MESSAGE, {
    createDocument: (src, opts) => new ChatMessage(src.id, opts),
    query: (ref) => ref
      .where('parent.id', '==', rootMessage.id)
      .orderBy('createdAt', 'desc')
      .limit(replyLimit),
  }), [rootMessage.id, replyLimit]);

  const orderedReplies = replies.docs.slice().reverse();
  const hasEarlierReplies = replies.docs.length === replyLimit;
  /*
    From this thread's own query, not the channel-wide window the inbox groups: a thread pushed out
    of that window would yield no row, and the mark on send would be dropped on exactly the busy
    channels this feature exists for.
  */
  const newestReplyAt = replies.docs.length > 0 ? replyTime(replies.docs[0]) : 0;

  /*
    Opening a thread deliberately does not mark it read: the coach reads a thread to decide whether
    to answer it, and a queue that empties on a glance is the problem the inbox exists to fix. Only
    sending a reply (`ChannelMessageInput`) or Mark as read (`ThreadInbox`) clears a thread.
  */
  return (
    <>
      <ParentSection>
        {renderMessage(rootMessage)}
      </ParentSection>
      <RepliesSection>
        {replies.isLoading
          ? <LoadingPage fullHeight={false} />
          : (
            <>
              {hasEarlierReplies && (
                <LoadEarlierButton
                  type="button"
                  onClick={() => setReplyLimit((limit) => limit + THREAD_REPLY_PAGE_SIZE)}
                >
                  {texts.loadEarlier}
                </LoadEarlierButton>
              )}
              {orderedReplies.map(renderMessage)}
            </>
          )}
      </RepliesSection>
      <ChannelMessageInput
        threadParent={rootMessage}
        threadNewestReplyAt={newestReplyAt}
      />
    </>
  );
});

ThreadModalContent.propTypes = {
  rootMessage: PropTypes.instanceOf(ChatMessage).isRequired,
  renderMessage: PropTypes.func.isRequired,
};

const ThreadModal = ({
  rootMessage,
  isOpen,
  onClose,
  renderMessage,
}) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: { overflowX: 'hidden' } }}
  >
    <Header>
      <Title>{texts.title}</Title>
      <CloseButton type="button" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </CloseButton>
    </Header>
    <ThreadModalContent
      rootMessage={rootMessage}
      renderMessage={renderMessage}
    />
  </Dialog>
);

ThreadModal.propTypes = {
  rootMessage: PropTypes.instanceOf(ChatMessage).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  renderMessage: PropTypes.func.isRequired,
};

export default ThreadModal;
